import React, { useMemo } from "react";
import cx from "classnames";
import type { TokenAutomation } from "hooks/firestore/models/tokenAutomation";
import type { Context } from "@/utilities/promotions/models/context";
import { Pill } from "@picklebet/ui";

import { ReactComponent as WinningsBoostIcon } from "@/features/betslip/assets/winnings-boost.svg";
import { ReactComponent as BetReloadIcon } from "@/features/betslip/assets/bet-reload.svg";

import type { TokenAllocations } from "@/features/betslip/hooks/useTokenAllocations";
import { automationTypeNames } from "@/features/betslip/utils/tokenFormatting";
import { ConditionalPill } from "@/features/betslip/components/parts/tokens/ConditionalPill";
import * as styles from "@/features/betslip/components/parts/tokens/PromoPills.module.scss";

type ActionType = NonNullable<TokenAutomation["actions"]>[number]["type"];

type AutomationPillsProps = {
  isLoading: boolean;
  isReviewingOrPlacing: boolean;
  selectionEventId: string;
  selectedAllocationId: string | null | undefined;
  selectedAutomationType: ActionType | undefined;
  sortedAllocations: TokenAllocations;
  sortedAutomations: TokenAutomation[];
  usedAllocations: Context["usedAllocations"];
  onSelect: (allocation: TokenAllocations[number]) => void;
  onDeselect: () => void;
};

const findFirstAllocationForType = (
  type: ActionType,
  allocations: TokenAllocations,
  automations: TokenAutomation[],
  usedAllocations: Context["usedAllocations"],
  selectionEventId: string,
): TokenAllocations[number] | undefined => {
  const free = allocations.find((allocation) => {
    const automation = automations.find(
      (a) => a.automationId === allocation.automationId,
    );
    const action = allocation.actions?.[0] ?? automation?.actions?.[0];
    return (
      action?.type === type &&
      typeof usedAllocations[allocation.id] === "undefined"
    );
  });
  if (free) return free;

  return allocations.find((allocation) => {
    const automation = automations.find(
      (a) => a.automationId === allocation.automationId,
    );
    const requirement = (automation?.requirements ?? []).find(
      (r) => !!r.userDrivenAttributes,
    );
    const isPerEventBased =
      !!requirement?.userDrivenAttributes?.timesAllowedToCompletePerEvent;
    const action = allocation.actions?.[0] ?? automation?.actions?.[0];
    if (isPerEventBased) {
      return (
        action?.type === type && allocation.usedAtEventId === selectionEventId
      );
    }
    return action?.type === type;
  });
};

export const AutomationPills = ({
  isLoading,
  isReviewingOrPlacing,
  selectionEventId,
  selectedAllocationId,
  selectedAutomationType,
  sortedAllocations,
  sortedAutomations,
  usedAllocations,
  onSelect,
  onDeselect,
}: AutomationPillsProps) => {
  const automationTypes = useMemo<ActionType[]>(() => {
    if (isLoading || !sortedAllocations.length) return [];
    return sortedAllocations.reduce<ActionType[]>((types, allocation) => {
      const automation = sortedAutomations.find(
        (a) => a.automationId === allocation.automationId,
      );
      const action = allocation?.actions?.[0] ?? automation?.actions?.[0];
      if (!action || types.includes(action.type)) return types;
      types.push(action.type);
      return types;
    }, []);
  }, [isLoading, sortedAllocations, sortedAutomations]);

  return (
    <>
      {automationTypes.map((type) => {
        const firstAllocation = findFirstAllocationForType(
          type,
          sortedAllocations,
          sortedAutomations,
          usedAllocations,
          selectionEventId,
        );
        if (!firstAllocation) return null;

        const isPressed = type === selectedAutomationType;

        const handleClick = () => {
          if (selectedAllocationId && isPressed) {
            onDeselect();
            return;
          }
          onSelect(firstAllocation);
        };

        if (type === "PAY_AS_WINNER") {
          return (
            <ConditionalPill
              key={`type-${type}`}
              disabled={isReviewingOrPlacing}
              pressed={isPressed}
              onClick={handleClick}
            >
              {automationTypeNames[type]}
            </ConditionalPill>
          );
        }

        const isWinningsBoost = type === "WINNINGS_BOOST";
        const Icon = isWinningsBoost ? WinningsBoostIcon : BetReloadIcon;

        return (
          <Pill
            key={`type-${type}`}
            className={cx(
              styles.tokenPill,
              isWinningsBoost ? styles.winningsBoost : styles.betReload,
            )}
            disabled={isReviewingOrPlacing}
            pressed={isPressed}
            overrideSvgFill={false}
            onClick={handleClick}
            onMouseDown={(e) => e.preventDefault()}
          >
            <span className={styles.icon}>
              <Icon />
            </span>
            {automationTypeNames[type]}
          </Pill>
        );
      })}
    </>
  );
};
